import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiTrash2, FiEdit2 } from "react-icons/fi";
import "../../styles/StreakConfigurations.css";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import AddStreak from "./AddStreak";
import EditStreak from "./EditStreak";
import ConfirmationDialog from "components/utils/ConfirmationDialog";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const API_URL_ORGINFO = `${process.env.REACT_APP_API_URL}/api/org-info`;

interface OrgInfo {
  org_uid: string;
  active_user_count: number;
  inactive_user_count: number;
  admins: string[];
}

interface StreakLevel {
  streak_level: number;
  min_streak_count: number;
  max_streak_count: number;
  reward_coins: number;
}
interface DefaultConfig {
  org_uid: string;
  possible_max_streak_count: number;
  min_reward_coins: number;
  max_reward_coins: number;
  streak_config: StreakLevel[];
}

const StreakConfigurations: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const navigate = useNavigate();
  const orgDetailsFromNav = location.state?.orgDetails;
  const [levels, setLevels] = useState<StreakLevel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [organizations, setOrganizations] = useState<OrgInfo[]>([]);
  const [defaultConfig, setDefaultConfig] = useState<DefaultConfig | null>(
    null
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [editLevel, setEditLevel] = useState<StreakLevel | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState<StreakLevel | null>(null);
  const [showEditDefaultModal, setShowEditDefaultModal] = useState(false);
  const [defaultConfigValues, setDefaultConfigValues] = useState({
    possible_max_streak_count:
      defaultConfig?.possible_max_streak_count?.toString() || "",
    min_reward_coins: defaultConfig?.min_reward_coins?.toString() || "",
    max_reward_coins: defaultConfig?.max_reward_coins?.toString() || "",
  });

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const privilege = localStorage.getItem("privilege");

  useEffect(() => {
    if (defaultConfig) {
      setDefaultConfigValues({
        possible_max_streak_count:
          defaultConfig.possible_max_streak_count?.toString() || "",
        min_reward_coins: defaultConfig.min_reward_coins?.toString() || "",
        max_reward_coins: defaultConfig.max_reward_coins?.toString() || "",
      });
    }
  }, [defaultConfig]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(API_URL_ORGINFO, {
          headers: {
            Accept: "application/json",
            "user-uid": userUid,
          }, withCredentials: true,
        });
        if (response.status === 200) {
          setOrganizations(response.data.data || []);
        } else {
          throw new Error("Failed to fetch organizations");
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchOrganizations();
  }, [sessionUid, userUid]);

  const fetchStreakConfigs = async () => {
    let orgUid = orgDetailsFromNav?.org_uid;
    if (privilege === "orgadmin" && !orgUid) {
      //if you store org_uid in localStorage or user profile
      orgUid = localStorage.getItem("org_uid");
    }

    try {
      const response = await axios.get(`${beAddress}/api/streak-config`, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
        params: {
          org_uid: orgUid,
        },
      });

      const configs = response.data.data?.[0]?.streak_config || [];
      setLevels(configs);
      setDefaultConfig(response.data.data[0]);
    } catch (error) {
      console.error("Error fetching streak configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  // when organizations are loaded, call streak-config API
  useEffect(() => {
    if (organizations.length > 0) {
      // pick the first org or loop through if multiple
      fetchStreakConfigs();
    }
  }, [organizations]);

  const handleDelete = async (streakLevel: number) => {
    const levelToDelete = levels.find(
      (level) => level.streak_level === streakLevel
    );
    if (!levelToDelete || !defaultConfig) return;

    try {
      const response = await axios.delete(`${beAddress}/api/streak-config`, {
        headers: {
          "user-uid": userUid,
        }, withCredentials: true,
        data: {
          streak_level: levelToDelete.streak_level,
          min_streak_count: levelToDelete.min_streak_count,
          max_streak_count: levelToDelete.max_streak_count,
          reward_coins: levelToDelete.reward_coins,
        },
      });

      if (response.status === 200 && response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.message || "Streak deleted successfully",
        });
        setLevels(levels.filter((level) => level.streak_level !== streakLevel));
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Failed to delete streak",
        });
      }
    } catch (error: any) {
      console.error("Error deleting streak config:", error);
      openNotification({
        type: "error",
        message: error.response?.data?.message || "Error deleting streak",
      });
    }
  };

  // const handleDelete = async (streakLevel: number) => {
  //   const levelToDelete = levels.find(
  //     (level) => level.streak_level === streakLevel
  //   );
  //   if (!levelToDelete || !defaultConfig) return;

  //   try {
  //     const response = await axios.delete(`${beAddress}/api/streak-config`, {
  //       headers: {
  //         "user-uid": userUid,
  //         "session-uid": sessionUid,
  //       },
  //       data: {
  //         // org_uid: defaultConfig.org_uid,
  //         streak_config_data: {
  //           streak_level: levelToDelete.streak_level,
  //           min_streak_count: levelToDelete.min_streak_count,
  //           max_streak_count: levelToDelete.max_streak_count,
  //           reward_coins: levelToDelete.reward_coins,
  //         },
  //       },
  //     });

  //     if (response.status === 200 && response.data?.status === "Success") {
  //       openNotification({
  //         type: "success",
  //         message: response.data.message || "Streak deleted successfully",
  //       });
  //       // remove from UI after success
  //       setLevels(levels.filter((level) => level.streak_level !== streakLevel));
  //     } else {
  //       openNotification({
  //         type: "error",
  //         message: response.data?.message || "Failed to delete streak",
  //       });
  //     }
  //   } catch (error: any) {
  //     console.error("Error deleting streak config:", error);
  //     openNotification({
  //       type: "error",
  //       message: error.response?.data?.message || "Error deleting streak",
  //     });
  //   }
  // };

  if (loading) return <p>Loading streak configurations...</p>;

  const confirmDelete = (level: StreakLevel) => {
    setLevelToDelete(level);
    setShowConfirmModal(true);
  };

  const updateDefaultConfig = async (data: {
    possible_max_streak_count: number;
    min_reward_coins: number;
    max_reward_coins: number;
  }) => {
    try {
      const response = await axios.put(
        `${beAddress}/api/default-streak-config`,
        data, // sending flat data directly
        {
          headers: {
            "user-uid": userUid,
          }, withCredentials: true,
        }
      );

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message:
            response.data.message || "Default config updated successfully",
        });
        // refresh default config in UI
        if (defaultConfig?.org_uid) fetchStreakConfigs();
        return true;
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Failed to update default config",
        });
        return false;
      }
    } catch (error: any) {
      console.error("Error updating default config:", error);
      openNotification({
        type: "error",
        message:
          error.response?.data?.message || "Error updating default config",
      });
      return false;
    }
  };
  return (
    <div className="streak-container">
      <h2 className="streak-title">Streaks Configuration</h2>

      <div className="streak-default-box">
        <div className="default-header">
          <p>
            <b>Default configurations</b>
          </p>
          {privilege !== "systemadmin" && (
            <>
              <button
                className="edit-default-btn"
                onClick={() => setShowEditDefaultModal(true)}
              >
                <FiEdit2 />
              </button>
            </>
          )}
        </div>
        <p>
          Possible max streak count: {defaultConfig?.possible_max_streak_count}
        </p>
        <p>Minimum reward coins: {defaultConfig?.min_reward_coins}</p>
        <p>Maximum reward coins: {defaultConfig?.max_reward_coins}</p>
      </div>

      {levels.map((level) => (
        <div key={level.streak_level} className="streak-card">
          <div className="streak-info">
            <div className="streak-item">
              <span className="label">Streak Level</span>
              <span className="value">{level.streak_level}</span>
            </div>

            <div className="streak-item">
              <span className="label">Min streak count</span>
              <span className="value">{level.min_streak_count}</span>
            </div>

            <div className="streak-item">
              <span className="label">Max streak count</span>
              <span className="value">{level.max_streak_count}</span>
            </div>

            <div className="streak-item">
              <span className="label">Reward coins on streak</span>
              <span className="value">
                <input
                  type="number"
                  value={level.reward_coins}
                  readOnly
                  className="streak-input"
                />{" "}
                coins
              </span>
            </div>
          </div>

          {privilege !== "systemadmin" && (
            <>
              <div className="streak-actions">
                <button
                  className="edit-btn"
                  onClick={() => {
                    setEditLevel(level);
                    setShowEditModal(true);
                  }}
                >
                  <FiEdit2 />
                </button>

                <button
                  className="delete-btn"
                  onClick={() => confirmDelete(level)}
                >
                  <FiTrash2 />
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      {privilege !== "systemadmin" && (
        <>
          <button className="add-btn" onClick={() => setShowAddModal(true)}>
            Add New Streak +
          </button>
        </>
      )}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="modal-close-btn"
              onClick={() => setShowAddModal(false)}
            >
              &times;
            </button>

            <AddStreak
              onClose={() => setShowAddModal(false)}
              onSuccess={() => {
                setShowAddModal(false);
                if (defaultConfig?.org_uid) {
                  fetchStreakConfigs();
                }
              }}
            />
          </div>
        </div>
      )}
      {showEditModal && editLevel && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="modal-close-btn"
              onClick={() => setShowEditModal(false)}
            >
              &times;
            </button>

            <EditStreak
              level={editLevel}
              orgUid={defaultConfig?.org_uid || ""}
              onClose={() => setShowEditModal(false)}
              onSuccess={() => {
                setShowEditModal(false);
                if (defaultConfig?.org_uid) {
                  fetchStreakConfigs(); // refresh list after edit
                }
              }}
            />
          </div>
        </div>
      )}
      {showConfirmModal && levelToDelete && (
        <ConfirmationDialog
          show={showConfirmModal}
          message={`Are you sure you want to delete streak level ${levelToDelete.streak_level}?`}
          onConfirm={() => {
            handleDelete(levelToDelete.streak_level);
            setShowConfirmModal(false);
            setLevelToDelete(null);
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setLevelToDelete(null);
          }}
          onClose={() => {
            setShowConfirmModal(false);
            setLevelToDelete(null);
          }}
        />
      )}
      {showEditDefaultModal && defaultConfig && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="modal-close-btn"
              onClick={() => setShowEditDefaultModal(false)}
            >
              &times;
            </button>

            <h3 className="modal-title">Edit Default Configurations</h3>

            <div className="form-group">
              <label>
                Possible Max Streak Count <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="number"
                value={defaultConfigValues.possible_max_streak_count}
                onChange={(e) =>
                  setDefaultConfigValues({
                    ...defaultConfigValues,
                    possible_max_streak_count: e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>
                Minimum Reward Coins <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="number"
                value={defaultConfigValues.min_reward_coins}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDefaultConfigValues({
                    ...defaultConfigValues,
                    min_reward_coins: e.target.value,
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>
                Maximum Reward Coins <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="number"
                value={defaultConfigValues.max_reward_coins}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDefaultConfigValues({
                    ...defaultConfigValues,
                    max_reward_coins: e.target.value,
                  })
                }
              />
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
              <button
                className="cancel-btn"
                onClick={() => setShowEditDefaultModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ccc",
                  padding: "10px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
              <button
                className="save-btn"
                style={{ flex: 1 }}
                onClick={async () => {
                  if (!defaultConfig) return;

                  // VALIDATION CHECK
                  if (
                    !defaultConfigValues.possible_max_streak_count ||
                    !defaultConfigValues.min_reward_coins ||
                    !defaultConfigValues.max_reward_coins
                  ) {
                    openNotification({
                      type: "error",
                      message: "Please fill in all required fields marked with *",
                    });
                    return;
                  }

                  const success = await updateDefaultConfig({
                    // REMOVED WRAPPER: Sending flat object
                    possible_max_streak_count:
                      parseInt(
                        defaultConfigValues.possible_max_streak_count,
                        10
                      ) || 0,
                    min_reward_coins:
                      parseInt(defaultConfigValues.min_reward_coins, 10) || 0,
                    max_reward_coins:
                      parseInt(defaultConfigValues.max_reward_coins, 10) || 0,
                  });

                  if (success) setShowEditDefaultModal(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreakConfigurations;